import { useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExcelDownloadVariant } from "helpers/types";

/**
 * The report's action row: Download PDF, Download Excel, Calculate and
 * Transactions.
 */
const ReportActions = ({
  isPdfLoading,
  pdfDisabled,
  pdfHint,
  onDownloadPdf,
  backendFiguresAvailable,
  liveFormulasAvailable,
  onDownloadExcel,
  isPreparingLiveExcel,
  isCalculating,
  onCalculate,
  onOpenTransactions,
}: {
  isPdfLoading: boolean;
  /** Whether the report on screen may not be exported -- downloadPdfAvailability. */
  pdfDisabled: boolean;
  /** Why it may not, shown on hover. */
  pdfHint: string;
  onDownloadPdf: () => void;
  /** Whether a calculated report's workbook is ready to download. */
  backendFiguresAvailable: boolean;
  /** Whether there is a report to build the live-formula workbook for -- the
   * run on screen, or the inputs on screen when none has been calculated
   * (task 5981). */
  liveFormulasAvailable: boolean;
  onDownloadExcel: (variant: ExcelDownloadVariant) => void;
  /** Whether the live-formula workbook is being generated for download --
   * the button shows a spinner and the menu's live item waits; Backend
   * Figures stays available. */
  isPreparingLiveExcel: boolean;
  /** Whether a run against the inputs on screen is in flight. */
  isCalculating: boolean;
  onCalculate: () => void;
  onOpenTransactions: () => void;
}) => {
  // Download Excel opens a menu of the two workbooks (task 5281); picking one
  // downloads it and closes the menu.
  const [excelMenuAnchor, setExcelMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const downloadExcel = (variant: ExcelDownloadVariant) => {
    setExcelMenuAnchor(null);
    onDownloadExcel(variant);
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 1 }}>
      <Grid item sx={{ mr: 2 }}>
        {/* A disabled button fires no events, so the span is what the
            tooltip listens to. */}
        <Tooltip title={pdfHint}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={onDownloadPdf}
              disabled={isPdfLoading || pdfDisabled}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <Grid item sx={{ mr: 2 }}>
        <Button
          id="download-excel-button"
          variant="contained"
          color="primary"
          onClick={(event) => setExcelMenuAnchor(event.currentTarget)}
          disabled={!backendFiguresAvailable && !liveFormulasAvailable}
          endIcon={
            isPreparingLiveExcel ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowDropDownIcon />
            )
          }
          aria-haspopup="menu"
          aria-controls={excelMenuAnchor ? "download-excel-menu" : undefined}
          aria-expanded={excelMenuAnchor ? "true" : undefined}
        >
          Download Excel
        </Button>
        <Menu
          id="download-excel-menu"
          anchorEl={excelMenuAnchor}
          open={Boolean(excelMenuAnchor)}
          onClose={() => setExcelMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{ "aria-labelledby": "download-excel-button" }}
        >
          <MenuItem
            onClick={() => downloadExcel("backend_figures")}
            disabled={!backendFiguresAvailable}
          >
            <ListItemText
              primary="Backend Figures"
              secondary={
                backendFiguresAvailable
                  ? "The calculated report, as values"
                  : "Calculate the report first"
              }
            />
          </MenuItem>
          {/* Only this item waits on the job it started: Backend Figures
              stays one click away throughout. */}
          <MenuItem
            onClick={() => downloadExcel("live_formulas")}
            disabled={!liveFormulasAvailable || isPreparingLiveExcel}
          >
            <ListItemText
              primary="Excel Live Formulas"
              secondary={
                isPreparingLiveExcel
                  ? "Preparing — the download starts when it is ready"
                  : "Formulas in place of values — built on request, takes a few minutes"
              }
            />
          </MenuItem>
        </Menu>
      </Grid>
      <Grid item sx={{ mr: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onCalculate()}
          disabled={isPdfLoading || isCalculating}
        >
          {isCalculating ? "Calculating…" : "Calculate"}
        </Button>
      </Grid>
      <Grid item sx={{ mr: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onOpenTransactions}
          disabled={isPdfLoading}
        >
          {isPdfLoading ? "Updating…" : "Transactions"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ReportActions;
